import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authClient } from '../lib/authClient';
import { useAdmin } from '../context/AdminContext';
import { useBandRole } from '../context/BandContext';
import { getPendingInvite } from '../utils/pendingInvite';
import { peekSettings } from '../utils/settingsCache';
import { useOfflineSession } from '../context/OfflineContext';
import { saveLastSession } from '../utils/lastSession';

export default function AuthSync() {
  const { data: session, isPending } = authClient.useSession();
  const navigate = useNavigate();
  const { setIsAdmin } = useAdmin();
  const { setBandRole, setTier } = useBandRole();
  const { offlineSession } = useOfflineSession();
  const synced = useRef(false);
  const lastUserId = useRef(null);
  // Saved with the offline session for the avatar; read through a ref so a rename doesn't re-sync
  const userName = useRef(null);
  userName.current = session?.user?.name;

  // A member removed from their band mid-session gets empty pages from then on, so as soon as
  // any API response says they have no band, take them to a page that says so (and signs them out).
  useEffect(() => {
    const check = res => {
      if (res?.headers?.['x-needs-onboarding'] !== '1') return;
      if (!window.location.pathname.startsWith('/dashboard')) return;
      setBandRole(null);
      setTier(null);
      navigate('/removed', { replace: true, state: { bandName: peekSettings()?.band_name } });
    };
    const id = axios.interceptors.response.use(
      res => { check(res); return res; },
      err => { check(err.response); return Promise.reject(err); }
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate, setBandRole, setTier]);

  useEffect(() => {
    const userId = session?.user?.id;

    // Reset when user signs out (offline mode keeps the saved role ProtectedRoute set)
    if (!userId) {
      if (offlineSession) return;
      synced.current = false;
      lastUserId.current = null;
      setBandRole(null);
      setTier(null);
      return;
    }

    // Skip if already synced for this user
    if (synced.current && lastUserId.current === userId) return;
    if (isPending) return;

    synced.current = true;
    lastUserId.current = userId;

    axios.get('/api/me')
      .then(res => {
        setBandRole(res.data.bandRole ?? null);
        setTier(res.data.subscriptionTier ?? null);
        // Lets the app open in offline mode at a venue with no signal
        if (res.data.artistId) saveLastSession({ ...res.data, name: userName.current });
        if (res.data.isAdmin) {
          setIsAdmin(true);
          if (window.location.pathname !== '/admin') navigate('/admin');
          return;
        }
        if (res.data.needsOnboarding) {
          // Someone who opened an invite link and then signed in finishes joining that band
          const target = getPendingInvite() ? '/join' : '/onboarding';
          // Leave the sign-in and sign-up pages alone, so a different account can be used
          const onAuthPage = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/email-verified'].includes(window.location.pathname);
          if (window.location.pathname !== target && !onAuthPage) navigate(target);
        }
      })
      .catch(() => {});
  }, [session?.user?.id, isPending, offlineSession, navigate, setIsAdmin, setBandRole, setTier]);

  return null;
}
